import React, { useEffect, useState } from "react";
import { Controller, set, SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import styles from "./OrdersFormModal.module.scss";
import { IFormInput, OptionsProps } from "../../types/orders";
import Select from "react-select";
import {
  getEditOrder,
  getEditPartyItems,
  toggleOrderModal,
} from "../../features/orders/ordersSlice";
import { selectStyles } from "../../static/styles";
import { FaCalendarAlt } from "react-icons/fa";
import moment from "moment";

type Props = {
  handleSaveOrder: (data: IFormInput) => void;
};

const OrdersFormModal = ({ handleSaveOrder }: Props) => {
  const dispatch = useAppDispatch();
  const editOrder = useAppSelector(getEditOrder);
  const editPartyItems = useAppSelector(getEditPartyItems);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemOption, setItemOption] = useState<OptionsProps[]>([]);
  const [processOption, setProcessOption] = useState<OptionsProps[]>([]);
  const [unitOption, setUnitOption] = useState<OptionsProps[]>([]);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 0,
      label: "ચાલુ કામ",
    },
    {
      value: 1,
      label: "પૂરું થયેલું કામ",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
    watch,
  } = useForm<IFormInput>();

  const unit_id = watch("unit_id");

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveOrder(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleOrderModal(false));
  };

  const handleChangeItem = (itemId: number) => {
    if (itemId) {
      setValue("item_id", itemId);
      clearErrors("item_id");
      setValue("process_id", 0);
      setValue("unit_id", 0);
      setValue("rate", 0);

      const item = editPartyItems.find((i) => i.id === itemId);

      const processOptions =
        item?.processes.map((process) => ({
          value: process.id,
          label: process.name,
        })) ?? [];

      setProcessOption(processOptions);
      setUnitOption([]);
    }
  };

  const handleChangeProcess = (processId: number) => {
    if (processId) {
      setValue("process_id", processId);
      clearErrors("process_id");
      setValue("unit_id", 0);
      setValue("rate", 0);

      const { item_id } = getValues();
      const item = editPartyItems.find((i) => i.id === item_id);

      const process = item?.processes.find((p) => p.id === processId);

      if (process) {
        const unitOptions =
          process.units
            .filter((unit) => unit.rate > 0)
            .map((unit) => ({
              value: unit.id,
              label: unit.name,
            })) ?? [];

        setUnitOption(unitOptions);
      }
    }
  };

  const handleChangeUnit = (unitId: number) => {
    if (unitId) {
      setValue("unit_id", unitId);
      clearErrors("unit_id");

      let rate = 0;

      const { process_id, item_id } = getValues();
      const item = editPartyItems.find((i) => i.id === item_id);
      const process = item?.processes.find((p) => p.id === process_id);

      if (process) {
        const unit = process.units.find((u) => u.id === unitId);

        if (unit) {
          rate = unit.rate;
        }
      }

      setValue("rate", rate);
    }
  };

  useEffect(() => {
    if (editOrder && editOrder.id) {
      reset({
        item_id: editOrder.item_id,
        process_id: editOrder.process_id,
        unit_id: editOrder.unit_id,
        qty: editOrder.qty,
        weight: editOrder.weight,
        status: editOrder.status,
        rate: editOrder.rate,
        design: editOrder.design,
        size: editOrder.size,
        cartoon: editOrder.cartoon,
        created_at: moment(editOrder.created_at).format("YYYY-MM-DDTHH:mm"),
      });
    } else {
      reset();
    }
  }, [editOrder, reset]);

  useEffect(() => {
    const itemOptions =
      editPartyItems?.map((item) => ({
        value: item.id,
        label: item.name,
      })) ?? [];

    let processOptions: OptionsProps[] = [];
    let unitOptions: OptionsProps[] = [];

    if (editOrder && editOrder.item_id) {
      const item = editPartyItems.find((i) => i.id === editOrder.item_id);

      processOptions =
        item?.processes.map((process) => ({
          value: process.id,
          label: process.name,
        })) ?? [];

      const process = item?.processes.find(
        (p) => p.id === editOrder.process_id
      );

      if (process) {
        unitOptions =
          process.units
            .filter((unit) => unit.rate > 0)
            .map((unit) => ({
              value: unit.id,
              label: unit.name,
            })) ?? [];
      }
    }

    setItemOption(itemOptions);
    setProcessOption(processOptions);
    setUnitOption(unitOptions);
  }, [editPartyItems, editOrder]);

  return (
    <div className={styles.OrdersFormModal}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
            ઓર્ડર ની માહિતી
          </h3>
          <div className="mt-2 py-3">
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <div className="grid gap-4">
                <div className="mt-6">
                  <label className="text-primary-1 font-bold">પાર્ટી :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="text"
                      placeholder="પાર્ટી નું નામ સિલેક્ટ કરો"
                      readOnly
                      value={
                        editOrder?.party?.firm_name +
                        (editOrder?.party?.name
                          ? " - " + editOrder?.party?.name
                          : "") +
                        (editOrder?.party?.phone
                          ? " (" + editOrder?.party?.phone + ")"
                          : "")
                      }
                    />
                  </div>
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">આઈટમ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("item_id", {
                        required: true,
                        min: 1,
                      })}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={
                            itemOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"આઈટમ નું નામ દાખલ કરો"}
                          options={itemOption}
                          onChange={(val: any) => handleChangeItem(val?.value)}
                        />
                      )}
                    />
                  </div>

                  {errors?.item_id && (
                    <span className="text-red-500">આઈટમ નું નામ દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">પ્રોસેસ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("process_id", {
                        required: true,
                        min: 1,
                      })}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={
                            processOption.find(
                              (val) => val.value === field?.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"પ્રોસેસ નું નામ દાખલ કરો"}
                          options={processOption}
                          onChange={(val: any) =>
                            handleChangeProcess(val?.value)
                          }
                        />
                      )}
                    />
                  </div>

                  {errors?.process_id && (
                    <span className="text-red-500">
                      પ્રોસેસ નું નામ દાખલ કરો
                    </span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">યુનિટ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("unit_id", {
                        required: true,
                        min: 1,
                      })}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={
                            unitOption.find(
                              (val) => val.value === field?.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"યુનિટ નું નામ દાખલ કરો"}
                          options={unitOption}
                          onChange={(val: any) => handleChangeUnit(val?.value)}
                        />
                      )}
                    />
                  </div>

                  {errors?.unit_id && (
                    <span className="text-red-500">યુનિટ નું નામ દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    {unit_id && unit_id == 3
                      ? "જોડી"
                      : unit_id && unit_id == 2
                      ? "નંગ"
                      : unit_id && unit_id == 1
                      ? "વજન"
                      : "વજન/નંગ/જોડી"}
                    :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      step={"any"}
                      placeholder={`${
                        unit_id && unit_id == 3
                          ? "જોડી"
                          : unit_id && unit_id == 2
                          ? "નંગ"
                          : unit_id && unit_id == 1
                          ? "વજન"
                          : "વજન/નંગ/જોડી"
                      } દાખલ કરો`}
                      {...register("qty", { required: true })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.qty && errors.qty.type === "required" && (
                    <span className="text-red-500">
                      {unit_id && unit_id == 3
                        ? "જોડી"
                        : unit_id && unit_id == 2
                        ? "નંગ"
                        : unit_id && unit_id == 1
                        ? "વજન"
                        : "વજન/નંગ/જોડી"}{" "}
                      દાખલ કરો
                    </span>
                  )}
                </div>

                {unit_id && unit_id > 1 && (
                  <div className="mt-1">
                    <label className="text-primary-1 font-bold">વજન :</label>
                    <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                      <input
                        className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        type="number"
                        step={"any"}
                        placeholder="વજન દાખલ કરો"
                        {...register("weight", {
                          required: true,
                        })}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                      />
                    </div>
                    {errors.weight && errors.weight.type === "required" && (
                      <span className="text-red-500">વજન દાખલ કરો</span>
                    )}
                  </div>
                )}

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">ડિઝાઇન :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      placeholder="ડિઝાઇન દાખલ કરો"
                      {...register("design", {
                        required: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.design && errors.design.type === "required" && (
                    <span className="text-red-500">ડિઝાઇન દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">માપ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      placeholder="માપ દાખલ કરો"
                      {...register("size", {
                        required: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.size && errors.size.type === "required" && (
                    <span className="text-red-500">માપ દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">બાચકા :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      placeholder="બાચકા દાખલ કરો"
                      {...register("cartoon", {
                        required: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.cartoon && errors.cartoon.type === "required" && (
                    <span className="text-red-500">બાચકા દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">
                    પ્રોસેસ રેટ :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="-?[0-9]*"
                      step={"any"}
                      placeholder="પ્રોસેસ રેટ દાખલ કરો"
                      {...register("rate", { required: true })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.rate && errors.rate.type === "required" && (
                    <span className="text-red-500">પ્રોસેસ રેટ દાખલ કરો</span>
                  )}
                </div>

                {/* <div className="mt-1">
                  <label className="text-primary-1 font-bold">અમાઉન્ટ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                    <input
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      pattern="-?[0-9]*"
                      step={"any"}
                      placeholder="અમાઉન્ટ દાખલ કરો"
                      {...register("amount", { required: true })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.amount && errors.amount.type === "required" && (
                    <span className="text-red-500">અમાઉન્ટ દાખલ કરો</span>
                  )}
                </div> */}

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">સ્ટેટ્સ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("status")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isDisabled={editOrder?.status === 0}
                          value={
                            statusOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"સ્ટેટ્સ નું નામ દાખલ કરો"}
                          options={statusOption}
                          onChange={(val: any) =>
                            setValue("status", val?.value)
                          }
                        />
                      )}
                    />
                  </div>

                  {errors?.status && (
                    <span className="text-red-500">
                      સ્ટેટ્સ નું નામ દાખલ કરો
                    </span>
                  )}
                </div>

                <div className="mt-1">
                  <label className="text-primary-1 font-bold">તારીખ :</label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1  placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="datetime-local"
                      {...register("created_at", { required: true })}
                      placeholder="ઓર્ડર ની તારીખ"
                    />
                  </div>

                  {errors?.created_at && (
                    <span className="text-red-500">
                      ઓર્ડર ની તારીખ સિલેક્ટ કરો
                    </span>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                  </button>
                </div>

                <div className="mt-1 text-center">
                  <button
                    className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="button"
                    onClick={handleCloseFormModal}
                    disabled={isLoading}
                  >
                    બંધ કરો
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersFormModal;
